import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getGame,
  saveCourseLessons,
  saveGameSettings,
  loadCourseVideo,
  loadSceneImage,
  loadPoster,
} from "../../redux/asyncThunk/userActions";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import { readChatAccessGateFromSettings } from "../../utils/chatAccessGate";
import { readAvailableMessengersFromSettings } from "../ProjectMessengerAvailability";
import ProjectTeamAccess from "../ProjectTeamAccess";
import { sanitizePlainText } from "../../utils/inputValidation";
import "./mobile-editor.css";

const { addMessage } = userSlice;

function normalizeLessonFromServer(lesson) {
  if (!lesson) return lesson;

  let quizQuestions = Array.isArray(lesson.quizQuestions)
    ? lesson.quizQuestions.map((question) => ({
        ...question,
        answers: [...(question.answers ?? [])],
      }))
    : [];

  if (!quizQuestions.length && lesson.quizEnabled) {
    const legacyText = lesson.quizQuestion?.trim();
    const legacyAnswers = lesson.quizAnswers ?? [];
    if (legacyText || legacyAnswers.length) {
      quizQuestions = [
        {
          id: `cq-${lesson.id}-legacy`,
          questionText: legacyText ?? "",
          answers: legacyAnswers.map((answer) => ({ ...answer })),
        },
      ];
    }
  }

  return {
    ...lesson,
    quizEnabled: Boolean(lesson.quizEnabled) || quizQuestions.length > 0,
    quizQuestions,
  };
}

function createEmptyLesson(order) {
  const id = `l-${Date.now()}`;
  return {
    id,
    order,
    title: "",
    content: "",
    contentMode: "text",
    hasVideo: false,
    screenpath: "",
    hasImage: false,
    imagepath: "",
    quizEnabled: false,
    quizQuestions: [],
  };
}

function createEmptyQuizQuestion(lessonId) {
  const id = `cq-${Date.now()}`;
  return {
    id,
    questionText: "",
    answers: [
      { id: `${id}-a1`, label: "", isCorrect: false },
      { id: `${id}-a2`, label: "", isCorrect: false },
    ],
  };
}

export default function MobileCourseEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.data?.user);
  const game = useSelector((state) => state.user.game) ?? initialGame;

  const [title, setTitle] = useState(game?.title ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [lessons, setLessons] = useState(
    (game?.courseLessons ?? []).map(normalizeLessonFromServer),
  );
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [showQuizEditor, setShowQuizEditor] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    projectStatus: getProjectStatusFromSettings(game?.settings),
    completion_text: game?.settings?.completion_text ?? "",
    author_telegram: game?.settings?.author_telegram ?? "",
    allow_replay: game?.settings?.allow_replay !== false,
  });
  const [availableMessengers, setAvailableMessengers] = useState(() =>
    readAvailableMessengersFromSettings(game?.settings),
  );
  const [chatAccessGate, setChatAccessGate] = useState(() =>
    readChatAccessGateFromSettings(game?.settings),
  );
  const [savingSettings, setSavingSettings] = useState(false);

  const lessonsSaveTimerRef = useRef(null);
  const settingsSaveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);
  const lastSavedSettingsRef = useRef(null);
  const loadedGameIdRef = useRef(game?._id ?? null);

  const sortedLessons = [...lessons].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const currentLesson = sortedLessons[currentLessonIndex] || null;
  const currentContentMode = currentLesson?.contentMode || "text";

  // Get questions for current lesson
  const currentQuestions = useMemo(
    () => currentLesson?.quizQuestions ?? [],
    [currentLesson?.quizQuestions],
  );
  const currentQuestion = currentQuestions[currentQuestionIndex] || null;

  const buildSettingsSnapshot = useCallback(
    () =>
      JSON.stringify({
        title: title.trim(),
        description,
        settings,
        availableMessengers,
        chatAccessGate,
      }),
    [title, description, settings, availableMessengers, chatAccessGate],
  );

  useEffect(() => {
    if (
      !game?._id ||
      (loadedGameIdRef.current === game._id && lastSavedSettingsRef.current)
    ) {
      return;
    }

    loadedGameIdRef.current = game._id;
    const nextSettings = {
      projectStatus: getProjectStatusFromSettings(game.settings),
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      allow_replay: game.settings?.allow_replay !== false,
    };
    const nextAvailableMessengers = readAvailableMessengersFromSettings(
      game.settings,
    );
    const nextChatAccessGate = readChatAccessGateFromSettings(game.settings);

    setTitle(game.title ?? "");
    setDescription(game.description ?? "");
    setSettings(nextSettings);
    setAvailableMessengers(nextAvailableMessengers);
    setChatAccessGate(nextChatAccessGate);
    lastSavedSettingsRef.current = JSON.stringify({
      title: (game.title ?? "").trim(),
      description: game.description ?? "",
      settings: nextSettings,
      availableMessengers: nextAvailableMessengers,
      chatAccessGate: nextChatAccessGate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reinitialize settings on course change
  }, [game?._id]);

  const persistSettings = useCallback(async () => {
    if (!game?._id) return false;
    const snapshot = buildSettingsSnapshot();
    setSavingSettings(true);
    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title: title.trim(),
          description,
          program: title.trim(),
          category: "",
          courseTheme: title.trim(),
          settings: mergeGameSettings(game.settings, {
            ...settingsFromProjectStatus(settings.projectStatus, game.settings),
            contentType: "course",
            completion_text: settings.completion_text,
            author_telegram: settings.author_telegram,
            allow_replay: settings.allow_replay,
            availableMessengers,
            ...chatAccessGate,
          }),
        }),
      ).unwrap();
      lastSavedSettingsRef.current = snapshot;
      return true;
    } catch {
      dispatch(addMessage({ type: "error", message: "Не удалось сохранить настройки" }));
      return false;
    } finally {
      setSavingSettings(false);
    }
  }, [
    buildSettingsSnapshot,
    dispatch,
    game?._id,
    game?.settings,
    title,
    description,
    settings,
    availableMessengers,
    chatAccessGate,
  ]);

  useEffect(() => {
    const snapshot = buildSettingsSnapshot();
    if (snapshot === lastSavedSettingsRef.current || !game?._id) return;

    clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      persistSettings();
    }, 800);

    return () => clearTimeout(settingsSaveTimerRef.current);
  }, [buildSettingsSnapshot, game?._id, persistSettings]);

  const handleSaveSettings = async () => {
    clearTimeout(settingsSaveTimerRef.current);
    const saved = await persistSettings();
    if (saved) setShowSettings(false);
  };

  // Auto-save
  useEffect(() => {
    if (!game?._id) return;

    const data = JSON.stringify(lessons);
    if (data === lastSavedRef.current) return;

    clearTimeout(lessonsSaveTimerRef.current);
    lessonsSaveTimerRef.current = setTimeout(() => {
      dispatch(
        saveCourseLessons({
          gameId: game._id,
          courseTheme: title.trim(),
          lessons,
        }),
      )
        .unwrap()
        .then(() => {
          lastSavedRef.current = data;
        })
        .catch(() => {
          dispatch(addMessage({ type: "error", message: "Ошибка сохранения" }));
        });
    }, 1500);

    return () => clearTimeout(lessonsSaveTimerRef.current);
  }, [lessons, game?._id, title, dispatch]);

  const handleAddLesson = () => {
    const newLesson = createEmptyLesson(sortedLessons.length);
    setLessons([...lessons, newLesson]);
  };

  const handleDeleteLesson = () => {
    if (sortedLessons.length <= 1) {
      dispatch(
        addMessage({ type: "error", message: "Должен быть минимум один урок" }),
      );
      return;
    }

    const updatedLessons = lessons.filter((l) => l.id !== currentLesson.id);
    setLessons(updatedLessons);

    if (currentLessonIndex > updatedLessons.length - 1) {
      setCurrentLessonIndex(Math.max(0, updatedLessons.length - 1));
    }

    setShowDeleteConfirm(null);
  };

  const handleNextLesson = () => {
    if (currentLessonIndex < sortedLessons.length - 1) {
      setCurrentLessonIndex(currentLessonIndex + 1);
      setShowQuizEditor(false);
    }
  };

  const handlePrevLesson = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex(currentLessonIndex - 1);
      setShowQuizEditor(false);
    }
  };

  const updateLesson = useCallback(
    (updates) => {
      setLessons((prevLessons) =>
        prevLessons.map((l) =>
          l.id === currentLesson?.id ? { ...l, ...updates } : l,
        ),
      );
    },
    [currentLesson?.id],
  );

  const handleContentModeChange = (mode) => {
    if (!currentLesson || !mode) return;
    updateLesson({ contentMode: mode });
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentLesson || !game?._id || !user?.id) return;

    setIsLoadingVideo(true);
    try {
      const result = await dispatch(
        loadCourseVideo({
          userId: user.id,
          gameId: game._id,
          lessonId: currentLesson.id,
          video: file,
        }),
      ).unwrap();

      updateLesson({
        hasVideo: true,
        screenpath: result.screenpath,
        contentMode: "video",
      });
    } catch {
      dispatch(addMessage({ type: "error", message: "Ошибка загрузки видео" }));
    } finally {
      setIsLoadingVideo(false);
      event.target.value = "";
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentLesson || !game?._id || !user?.id) return;

    setIsLoadingImage(true);
    try {
      const result = await dispatch(
        loadSceneImage({
          userId: user.id,
          gameId: game._id,
          lessonId: currentLesson.id,
          image: file,
        }),
      ).unwrap();

      updateLesson({
        hasImage: true,
        imagepath: result.imagepath,
        contentMode: "image",
      });
    } catch {
      dispatch(
        addMessage({ type: "error", message: "Ошибка загрузки изображения" }),
      );
    } finally {
      setIsLoadingImage(false);
      event.target.value = "";
    }
  };

  const handleAddQuestion = () => {
    // Prevent adding question if the last question is empty
    if (currentQuestions.length > 0) {
      const lastQuestion = currentQuestions[currentQuestions.length - 1];
      if (!lastQuestion.questionText.trim()) {
        return;
      }
    }

    const newQuestion = createEmptyQuizQuestion(currentLesson.id);
    updateLesson({
      quizEnabled: true,
      quizQuestions: [...currentQuestions, newQuestion],
    });
    setCurrentQuestionIndex(currentQuestions.length);
  };

  const handleDeleteQuestion = () => {
    if (currentQuestions.length <= 1) {
      updateLesson({
        quizEnabled: false,
        quizQuestions: [],
      });
      setShowQuizEditor(false);
      return;
    }

    const updatedQuestions = currentQuestions.filter(
      (_, idx) => idx !== currentQuestionIndex,
    );
    updateLesson({ quizQuestions: updatedQuestions });

    if (currentQuestionIndex > updatedQuestions.length - 1) {
      setCurrentQuestionIndex(Math.max(0, updatedQuestions.length - 1));
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const updateQuestion = useCallback(
    (updates) => {
      const updatedQuestions = currentQuestions.map((q, idx) =>
        idx === currentQuestionIndex ? { ...q, ...updates } : q,
      );
      updateLesson({ quizQuestions: updatedQuestions });
    },
    [currentQuestionIndex, currentQuestions, updateLesson],
  );

  const updateAnswer = useCallback(
    (answerIndex, updates) => {
      const updatedAnswers = currentQuestion.answers.map((a, idx) =>
        idx === answerIndex ? { ...a, ...updates } : a,
      );
      updateQuestion({ answers: updatedAnswers });
    },
    [currentQuestion, updateQuestion],
  );

  const handlePoster = (event) => {
    const file = event.target.files?.[0];
    if (!file || !game?._id) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      dispatch(
        loadPoster({
          gameId: game._id,
          base64String: reader.result,
          onSucces: () => dispatch(getGame({ gameId: game._id })),
        }),
      );
    };
  };

  const handleChatAccessChange = (gatePatch) => {
    setChatAccessGate((current) => ({ ...current, ...gatePatch }));
  };

  if (!currentLesson) {
    return (
      <div className="mobile-editor__empty">
        <p>Нет уроков для редактирования</p>
        <Button variant="contained" onClick={handleAddLesson}>
          Добавить урок
        </Button>
      </div>
    );
  }

  return (
    <div className="mobile-editor mobile-editor--course">
      {/* Course title header */}
      <div className="mobile-editor__header">
        <TextField
          fullWidth
          label="Название курса"
          value={title}
          onChange={(e) => setTitle(sanitizePlainText(e.target.value, 120))}
          className="mobile-editor__title-field"
          size="small"
        />
        <IconButton
          onClick={() => setShowSettings(true)}
          size="small"
          title="Настройки проекта"
          aria-label="Настройки проекта"
        >
          <SettingsOutlinedIcon />
        </IconButton>
      </div>

      {/* Lesson navigation */}
      <div className="mobile-editor__nav-section">
        <div className="mobile-editor__title">
          <h2>
            Урок {currentLessonIndex + 1} из {sortedLessons.length}
          </h2>
        </div>

        <div className="mobile-editor__nav">
          <IconButton
            onClick={handlePrevLesson}
            disabled={currentLessonIndex === 0}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <span className="mobile-editor__page">
            {currentLessonIndex + 1}/{sortedLessons.length}
          </span>
          <IconButton
            onClick={handleNextLesson}
            disabled={currentLessonIndex === sortedLessons.length - 1}
            size="small"
          >
            <ArrowForwardIcon />
          </IconButton>
          <IconButton
            onClick={handleAddLesson}
            size="small"
            title="Добавить урок"
          >
            <AddIcon />
          </IconButton>
        </div>
      </div>

      {!showQuizEditor ? (
        <>
          {/* Lesson content */}
          <Card className="mobile-editor__card">
            <CardContent className="mobile-editor__lesson-content">
              <TextField
                fullWidth
                label="Название урока"
                value={currentLesson.title}
                onChange={(e) => updateLesson({ title: e.target.value })}
                className="mobile-editor__field"
                margin="normal"
                size="small"
              />

              {/* Content mode selector */}
              <div className="mobile-editor__mode-selector">
                <label className="mobile-editor__mode-label">
                  Формат содержания:
                </label>
                <ToggleButtonGroup
                  value={currentContentMode}
                  exclusive
                  onChange={(e, newMode) =>
                    newMode && handleContentModeChange(newMode)
                  }
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="text">
                    <TextFieldsOutlinedIcon />
                  </ToggleButton>
                  <ToggleButton value="video">
                    <VideocamOutlinedIcon />
                  </ToggleButton>
                  <ToggleButton value="image">
                    <ImageOutlinedIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              {/* Content based on mode */}
              {currentContentMode === "text" && (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Содержание урока"
                  value={currentLesson.content}
                  onChange={(e) => updateLesson({ content: e.target.value })}
                  className="mobile-editor__field"
                  margin="normal"
                  placeholder="Введите текст урока"
                />
              )}

              {currentContentMode === "video" && (
                <>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Описание видео (опционально)"
                    value={currentLesson.content}
                    onChange={(e) => updateLesson({ content: e.target.value })}
                    className="mobile-editor__field"
                    margin="normal"
                  />

                  {currentLesson.hasVideo ? (
                    <div className="mobile-editor__video-preview">
                      <div className="mobile-editor__video-placeholder">
                        <VideocamOutlinedIcon sx={{ fontSize: 48 }} />
                        <p>Видео загружено</p>
                      </div>
                      <Button
                        size="small"
                        startIcon={<VideocamOutlinedIcon />}
                        onClick={() =>
                          document.getElementById("course-video-input").click()
                        }
                      >
                        Изменить видео
                      </Button>
                      <input
                        id="course-video-input"
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        style={{ display: "none" }}
                      />
                    </div>
                  ) : (
                    <InputFileUpload
                      onChange={handleVideoUpload}
                      accept="video/*"
                      label="Загрузить видео"
                      disabled={isLoadingVideo}
                    />
                  )}
                </>
              )}

              {currentContentMode === "image" && (
                <>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Описание изображения (опционально)"
                    value={currentLesson.content}
                    onChange={(e) => updateLesson({ content: e.target.value })}
                    className="mobile-editor__field"
                    margin="normal"
                  />

                  {currentLesson.hasImage ? (
                    <div className="mobile-editor__image-preview">
                      <div className="mobile-editor__image-placeholder">
                        <ImageOutlinedIcon sx={{ fontSize: 48 }} />
                        <p>Изображение загружено</p>
                      </div>
                      <Button
                        size="small"
                        startIcon={<ImageOutlinedIcon />}
                        onClick={() =>
                          document.getElementById("course-image-input").click()
                        }
                      >
                        Изменить изображение
                      </Button>
                      <input
                        id="course-image-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: "none" }}
                      />
                    </div>
                  ) : (
                    <InputFileUpload
                      onChange={handleImageUpload}
                      accept="image/*"
                      label="Загрузить изображение"
                      disabled={isLoadingImage}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Quiz button */}
          {currentLesson.quizEnabled && currentQuestions.length > 0 && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setShowQuizEditor(true);
                setCurrentQuestionIndex(0); // Show first question automatically
              }}
              className="mobile-editor__btn-quiz"
            >
              Редактировать тест ({currentQuestions.length}{" "}
              {currentQuestions.length === 1 ? "вопрос" : "вопросов"})
            </Button>
          )}

          {!currentLesson.quizEnabled ? (
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddQuestion}
              className="mobile-editor__btn-add-answer"
            >
              Добавить тест
            </Button>
          ) : null}

          {/* Lesson actions */}
          <div className="mobile-editor__lesson-actions">
            {sortedLessons.length > 1 && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setShowDeleteConfirm("lesson")}
              >
                Удалить урок
              </Button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Quiz editor */}
          {currentQuestion && (
            <>
              <div className="mobile-editor__nav-section">
                <div className="mobile-editor__title">
                  <h3>
                    Вопрос {currentQuestionIndex + 1} из{" "}
                    {currentQuestions.length}
                  </h3>
                </div>
                <div className="mobile-editor__nav">
                  <IconButton
                    onClick={handlePrevQuestion}
                    disabled={currentQuestionIndex === 0}
                    size="small"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <span className="mobile-editor__page">
                    {currentQuestionIndex + 1}/{currentQuestions.length}
                  </span>
                  <IconButton
                    onClick={handleNextQuestion}
                    disabled={
                      currentQuestionIndex === currentQuestions.length - 1
                    }
                    size="small"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </div>
              </div>

              <Card className="mobile-editor__card">
                <CardContent>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Текст вопроса"
                    value={currentQuestion.questionText}
                    onChange={(e) =>
                      updateQuestion({ questionText: e.target.value })
                    }
                    margin="normal"
                  />

                  <div className="mobile-editor__answers-section">
                    <h4>Варианты ответов</h4>
                    {currentQuestion.answers.map((answer, answerIndex) => (
                      <Card
                        key={answer.id}
                        className="mobile-editor__answer-item"
                      >
                        <CardContent className="mobile-editor__answer-content">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={answer.isCorrect}
                                onChange={(e) =>
                                  updateAnswer(answerIndex, {
                                    isCorrect: e.target.checked,
                                  })
                                }
                              />
                            }
                            label="Правильный ответ"
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label={`Ответ ${answerIndex + 1}`}
                            value={answer.label}
                            onChange={(e) =>
                              updateAnswer(answerIndex, {
                                label: e.target.value,
                              })
                            }
                            margin="normal"
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {currentQuestion.answers.length < 5 && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newAnswer = {
                          id: `${currentQuestion.id}-a${Date.now()}`,
                          label: "",
                          isCorrect: false,
                        };
                        updateQuestion({
                          answers: [...currentQuestion.answers, newAnswer],
                        });
                      }}
                      className="mobile-editor__btn-add-answer"
                    >
                      Добавить ответ
                    </Button>
                  )}
                </CardContent>
              </Card>

              <div className="mobile-editor__question-actions">
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                >
                  Добавить вопрос
                </Button>
                {currentQuestions.length > 1 && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleDeleteQuestion}
                  >
                    Удалить вопрос
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setShowQuizEditor(false)}
                >
                  Назад к уроку
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {/* Dialog: Confirm delete lesson */}
      <Dialog
        open={showDeleteConfirm === "lesson"}
        onClose={() => setShowDeleteConfirm(null)}
      >
        <DialogTitle>Удалить урок?</DialogTitle>
        <DialogContent>
          <p>
            Вы уверены, что хотите удалить этот урок? Это действие нельзя
            отменить.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(null)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteLesson}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Course settings */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        fullScreen
        className="mobile-course-settings"
      >
        <DialogTitle className="mobile-course-settings__title">
          <span>Настройки курса</span>
          <IconButton
            onClick={() => setShowSettings(false)}
            aria-label="Закрыть настройки"
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="mobile-course-settings__content">
          <section className="mobile-course-settings__card">
            <TextField
              label="Название курса"
              size="small"
              fullWidth
              className="mobile-editor__field"
              value={title}
              onChange={(event) =>
                setTitle(sanitizePlainText(event.target.value, 120))
              }
            />
            <TextField
              label="Приветственный текст"
              size="small"
              fullWidth
              multiline
              minRows={3}
              className="mobile-editor__field"
              value={description}
              onChange={(event) =>
                setDescription(sanitizePlainText(event.target.value, 2000))
              }
            />
            <div className="mobile-course-settings__fields-row">
              <TextField
                select
                label="Статус"
                size="small"
                fullWidth
                className="mobile-editor__field"
                value={settings.projectStatus}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    projectStatus: event.target.value,
                  }))
                }
              >
                <MenuItem value="draft">Черновик</MenuItem>
                <MenuItem value="published">Опубликован</MenuItem>
                <MenuItem value="hidden">Скрыт</MenuItem>
              </TextField>
              <TextField
                label="Telegram HR"
                size="small"
                fullWidth
                className="mobile-editor__field"
                value={settings.author_telegram}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    author_telegram: sanitizePlainText(event.target.value, 80),
                  }))
                }
              />
            </div>
            <div className="mobile-course-settings__replay">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.allow_replay}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        allow_replay: event.target.checked,
                      }))
                    }
                  />
                }
                label="Разрешить повторное прохождение курса"
              />
              <p>
                {settings.allow_replay
                  ? "Сотрудник сможет пройти курс заново после завершения."
                  : "После завершения курс будет доступен только для просмотра прогресса — повторно пройти нельзя."}
              </p>
            </div>
            <div className="mobile-course-settings__poster">
              <InputFileUpload
                handleAddPoster={handlePoster}
                accept="image/*"
                label="Загрузить обложку курса"
                fullWidth
              />
            </div>
          </section>
          <ProjectTeamAccess
            gameId={game._id}
            value={availableMessengers}
            onChange={setAvailableMessengers}
            chatAccessSettings={chatAccessGate}
            onChatAccessChange={handleChatAccessChange}
          />
        </DialogContent>
        <DialogActions className="mobile-course-settings__actions">
          <Button
            variant="outlined"
            className="app-btn app-btn--secondary"
            onClick={() => setShowSettings(false)}
          >
            Закрыть
          </Button>
          <Button
            variant="contained"
            className="app-btn app-btn--primary"
            disabled={savingSettings}
            onClick={handleSaveSettings}
          >
            {savingSettings ? "Сохранение…" : "Сохранить настройки"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
